import {
  ADMIN_MENU,
  AdminDollarInput2,
  ApplyMenuPriceFixDto,
  Box,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  lightTheme,
  MenuPriceFixCandidate,
  TextButton,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  BLOCKED_REASON_TEXT,
  describeCurrentPrices,
  describePriceChange,
  dishOnBrand,
} from '../helpers/menuPriceFixLabels';
import { ApproveMenuPriceModal } from './ApproveMenuPriceModal';
import { InlineAction } from './SupportTicketModal.styled';

type Props = {
  candidate: MenuPriceFixCandidate;
  /** False on somebody else's ticket: the server would refuse it. */
  canApprove: boolean;
  busy: boolean;
  onApprove: (dto: ApplyMenuPriceFixDto) => Promise<boolean>;
  onOpen: (path: string) => void;
};

/**
 * One dish the merchant may mean, with the new prices to approve for it.
 *
 * Both fields are integer cents end to end (`AdminDollarInput2` edits cents
 * digit by digit), so nothing is ever multiplied from a float. Delivery starts
 * at the backend's suggestion, or empty when it has none. Pickup starts at its
 * suggestion, which only exists while pickup equals delivery; otherwise it
 * shows today's pickup price and is sent only once support changes it - left
 * out, the server keeps pickup as it is.
 *
 * The caller keys this on the live prices, so a refetch that moves them
 * starts the row over from the new numbers instead of a stale draft.
 */
export const MenuPriceCandidate = ({
  candidate,
  canApprove,
  busy,
  onApprove,
  onOpen,
}: Props) => {
  const [price, setPrice] = useState(candidate.suggestedPrice ?? 0);
  const [pickup, setPickup] = useState(
    candidate.suggestedPickupPrice ?? candidate.pickupPrice ?? 0
  );
  const [pickupEdited, setPickupEdited] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const blocked = candidate.blockedReason;
  /* Zero is an empty field, and pickup cannot be cleared from here - so an
     emptied pickup means "leave it" rather than a price of nothing. */
  const pickupPrice =
    (pickupEdited || candidate.suggestedPickupPrice !== null) && pickup > 0
      ? pickup
      : undefined;
  const unchanged =
    price === candidate.price &&
    (pickupPrice === undefined || pickupPrice === candidate.pickupPrice);
  const problem =
    price === 0
      ? 'Enter the new delivery price first.'
      : unchanged
      ? 'This dish already has that price.'
      : null;

  const confirm = async () => {
    const ok = await onApprove({
      itemId: candidate.itemId,
      price,
      ...(pickupPrice !== undefined && { pickupPrice }),
    });
    if (ok) setConfirming(false);
  };

  const menuPath = `${ADMIN_MENU.replace(
    ':locationId',
    String(candidate.locationId)
  ).replace(':brandId', String(candidate.brandId))}?menuId=${candidate.menuId}`;

  const disabled = !canApprove || !!blocked;

  return (
    <Box $boxNoShadow>
      <H5 $m="0">{dishOnBrand(candidate)}</H5>
      <Disclaimer $m="4px 0 0 0" $color={lightTheme.lightGray}>
        {candidate.menuName} menu · {describeCurrentPrices(candidate)}
      </Disclaimer>

      <Div $flex $gap="12px" $mt="16" $columnSM>
        <AdminDollarInput2
          label="New delivery price"
          value={price}
          disabled={disabled}
          onValueChange={(cents: number) => setPrice(cents)}
          $textCenter
        />
        <AdminDollarInput2
          label="New pickup price"
          value={pickup}
          disabled={disabled}
          onValueChange={(cents: number) => {
            setPickup(cents);
            setPickupEdited(true);
          }}
          $textCenter
        />
      </Div>

      {blocked ? (
        <Div $flex $between $alignCenter $gap="12px" $mt="12">
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {BLOCKED_REASON_TEXT[blocked]}
          </Disclaimer>
          {blocked === 'providerPrices' && (
            <InlineAction>
              <TextButton
                color={lightTheme.primary}
                $underline
                onClick={() => onOpen(menuPath)}
              >
                Open menu editor
              </TextButton>
            </InlineAction>
          )}
        </Div>
      ) : (
        <Div $mt="16">
          {/* aria-disabled rather than disabled while the numbers are wrong,
              so the click can say what is wrong - as the reply field does. */}
          <Button
            $selected
            $w="100%"
            $wSM="100%"
            disabled={!canApprove || busy}
            aria-disabled={!!problem}
            onClick={() =>
              problem ? ErrorToast(problem) : setConfirming(true)
            }
          >
            Approve
          </Button>
        </Div>
      )}

      <ApproveMenuPriceModal
        isOpen={confirming}
        change={describePriceChange(candidate, price, pickupPrice)}
        busy={busy}
        onConfirm={confirm}
        onClose={() => setConfirming(false)}
      />
    </Box>
  );
};
